// Onboarding API service - connects to backend at localhost:8084
import API_BASE_URL from '../config';

const BASE = API_BASE_URL; // e.g. http://localhost:8084/api/v1.0

// Helper for JSON requests
async function request(url, options = {}) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `Request failed: ${res.status}`);
    err.status = res.status;
    Object.assign(err, data);
    throw err;
  }
  return data;
}

// ─── Owner Registration & Verification ───────────────────────────────────────

export async function registerOwner({ ownerName, contactEmail, contactPhone, password }) {
  return request(`${BASE}/owners/register`, {
    method: 'POST',
    body: JSON.stringify({ ownerName, contactEmail, contactPhone, password }),
  });
}

export async function verifyOwnerEmail({ email, otp }) {
  return request(`${BASE}/owners/verify-email`, {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

export async function resendOwnerOtp({ email }) {
  return request(`${BASE}/owners/resend-otp`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function checkVerificationStatus(email) {
  return request(`${BASE}/owners/verify-status/${encodeURIComponent(email)}`);
}

export async function getOwnerDetails(email) {
  return request(`${BASE}/owners/details?email=${encodeURIComponent(email)}`);
}

// ─── Owner Auth (against owners table) ───────────────────────────────────────

export async function loginOwner({ email, password }) {
  return request(`${BASE}/owners/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function getOwnerOnboardingProgress(email) {
  return request(`${BASE}/owners/onboarding-progress?email=${encodeURIComponent(email)}`);
}

// ─── Multiple Business Onboarding ────────────────────────────────────────────

export async function createMultipleBusinessOnboarding(payload) {
  return request(`${BASE}/api/onboarding/multiple-business/create`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getOwnerBusinesses(ownerId) {
  return request(`${BASE}/api/onboarding/multiple-business/owner/${ownerId}/businesses`);
}

// ─── Subscription Plans ──────────────────────────────────────────────────────

export async function getSubscriptionPlans() {
  return request(`${BASE}/subscription/plans`);
}

export async function getSubscriptionPlansByType(packageType) {
  return request(`${BASE}/subscription/plans/by-package-type?packageType=${encodeURIComponent(packageType)}`);
}

export async function getPricingPlans() {
  return request(`${BASE}/api/pricing/plans`);
}

export async function getSubscriptionDurations() {
  return request(`${BASE}/subscription/durations`);
}

// ─── Subscription ────────────────────────────────────────────────────────────

export async function createOwnerSubscription(payload) {
  return request(`${BASE}/subscription/subscribe`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ─── Payment (Razorpay) ─────────────────────────────────────────────────────

export async function createRazorpayOrder({ amount, currency = 'INR' }) {
  return request(`${BASE}/payment/razorpay/create-order`, {
    method: 'POST',
    body: JSON.stringify({ amount, currency }),
  });
}

export async function verifyRazorpayPayment({ razorpay_order_id, razorpay_payment_id, razorpay_signature }) {
  return request(`${BASE}/payment/razorpay/verify`, {
    method: 'POST',
    body: JSON.stringify({ razorpay_order_id, razorpay_payment_id, razorpay_signature }),
  });
}

export async function createPaymentRecord(payload) {
  return request(`${BASE}/payment/create`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updatePaymentStatus(paymentId, { paymentStatus, transactionId }) {
  return request(`${BASE}/payment/${paymentId}/update-status`, {
    method: 'PUT',
    body: JSON.stringify({ paymentStatus, transactionId }),
  });
}

// ─── Public Restaurant Onboarding ────────────────────────────────────────────

export async function createRestaurantOnboarding(payload) {
  return request(`${BASE}/owners/onboarding/restaurants`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ─── WhatsApp OTP ────────────────────────────────────────────────────────────

export async function sendWhatsAppOtp({ email, phone }) {
  return request(`${BASE}/owners/send-whatsapp-otp`, {
    method: 'POST',
    body: JSON.stringify({ email, phone }),
  });
}

export async function verifyWhatsAppOtp({ email, otp }) {
  return request(`${BASE}/owners/verify-whatsapp-otp`, {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

// ─── Shop Categories ─────────────────────────────────────────────────────────

export async function getShopCategories() {
  return request(`${BASE}/api/shop-categories`);
}

// ─── Razorpay Key (test) ─────────────────────────────────────────────────────
export const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SaF9d2Z5pYOSr5';
